import { Component, OnInit } from '@angular/core';
import { UserService } from '../services/user.service';
import { DatabaseService } from '../services/database.service';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { AlertService } from '../services/alert.service';
import { CsvService } from '../services/csv.service';
import { MatDialog } from '@angular/material/dialog';
import { CsvUploadDialogComponent } from '../csv-upload-dialog/csv-upload-dialog.component';
import { EventMetricsDialogComponent } from '../event-metrics-dialog/event-metrics-dialog.component';

@Component({
  selector: 'app-admin-events',
  templateUrl: './admin-events.component.html',
  styleUrls: ['./admin-events.component.scss'],
  standalone: false
})
export class AdminEventsComponent implements OnInit {
  allEvents: any[] = [];
  currentEvents: any[] = [];
  loading: boolean = true;
  user: any;
  activeFilter: 'all' | 'pending' | 'approved' = 'all';
  dateCategoryFilters = {
    past: true,
    today: true,
    future: true
  };
  searchQuery = '';
  statusDropdownOpen = false;
  availableStatuses: string[] = [];
  statusFilters: Record<string, boolean> = {};

  constructor(
    private _user: UserService,
    private _db: DatabaseService,
    private router: Router,
    private location: Location,
    private _alert: AlertService,
    private _csv: CsvService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    // Check if user is admin and load events
    this._user.user$.subscribe((user) => {
      if (user) {
        this.user = user;
        
        if (this.user.isAdmin) {
          this.loadCurrentEvents();
        } else {
          this.loading = false;
        }
      }
    });
  }

  loadCurrentEvents(): void {
    this.loading = true;
    
    // Get all events from the database
    this._db.getAllEvents().subscribe({
      next: (response) => {
        try {
          // Normalize possible response envelope
          let events = response;
          if (events && typeof events === 'object') {
            events = events.events || events; // unwrap if API returns { events: [...] }
          }

          // Print all events for debugging
          console.log('Raw events data received from API:', events);

          if (!Array.isArray(events)) {
            this.allEvents = [];
            this.currentEvents = [];
            return;
          }

          const today = new Date();
          today.setHours(0,0,0,0);

          const processed = events
            .map((event: any) => {
              let dateCategory: 'past' | 'today' | 'future' = 'future';
              if (event?.start_date) {
                const start = new Date(event.start_date);
                const startDay = new Date(start);
                startDay.setHours(0,0,0,0);
                if (startDay.getTime() === today.getTime()) {
                  dateCategory = 'today';
                } else if (startDay.getTime() < today.getTime()) {
                  dateCategory = 'past';
                }
              }
              const rawRequestor = this.getPersonDisplay(event?.requestor) || this.getPersonDisplay(event?.event_leader);
              // Normalize casing: if looks like email (contains @) make lowercase, else leave as-is
              const normalizedRequestor = rawRequestor.includes('@')
                ? rawRequestor.toLowerCase()
                : rawRequestor;
              const workflow = String(event?.workflow || '').toLowerCase();
              const requestStatus = String(event?.request_status || '').toLowerCase();
              const isUnsubmitted = workflow === 'new' || requestStatus === 'new';
              const status = isUnsubmitted ? 'Unsubmitted' : (event.request_status || event.workflow || 'Pending');
              const statusKey = this.normalizeStatusKey(status);
              return {
                ...event,
                formattedStartDate: event.start_date, // support existing helper methods
                dateCategory,
                displayTitle: event.request_title || event.title || '',
                displayDate: this.formatDate(event.start_date),
                displayTime: this.formatTime(event.start_date, event.end_date),
                originalRequestor: rawRequestor,
                requestor: normalizedRequestor,
                status,
                statusKey
              };
            })
            // Sort: today first, then upcoming future chronologically, then past (most recent past first)
            .sort((a: any, b: any) => {
              const orderRank = (cat: string) => cat === 'today' ? 0 : (cat === 'future' ? 1 : 2);
              const rankDiff = orderRank(a.dateCategory) - orderRank(b.dateCategory);
              if (rankDiff !== 0) return rankDiff;
              const aTime = new Date(a.start_date || a.formattedStartDate).getTime();
              const bTime = new Date(b.start_date || b.formattedStartDate).getTime();
              return a.dateCategory === 'past' ? bTime - aTime : aTime - bTime; // reverse chronological for past
            });

          this.allEvents = processed;
          this.updateStatusFilters(processed);
          this.applyFilter();
        } catch (error) {
          console.error('Error processing events:', error);
          this.allEvents = [];
          this.currentEvents = [];
        } finally {
          this.loading = false;
        }
      },
      error: (error) => {
        console.error('Error loading events:', error);
        this.allEvents = [];
        this.currentEvents = [];
        this.loading = false;
      }
    });
  }

  private getPersonDisplay(person: any): string {
    if (!person) return '';
    if (typeof person === 'string') return person;
    if (typeof person === 'object') {
      return String(person.name || person.displayName || person.email || person.gmid || '');
    }
    return String(person);
  }

  setFilter(filter: 'all' | 'pending' | 'approved'): void {
    this.activeFilter = filter;
    this.syncStatusFiltersFromActiveFilter();
    this.applyFilter();
  }

  toggleDateCategory(category: 'past' | 'today' | 'future'): void {
    this.dateCategoryFilters[category] = !this.dateCategoryFilters[category];
    this.applyFilter();
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchQuery = target?.value || '';
    this.applyFilter();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.applyFilter();
  }

  private applyFilter(): void {
    let filtered = [...this.allEvents];

    if (this.activeFilter === 'pending') {
      filtered = filtered.filter(event =>
        event?.status?.toLowerCase() === 'pending'
      );
    } else if (this.activeFilter === 'approved') {
      filtered = filtered.filter(event =>
        event?.status?.toLowerCase() === 'approved'
      );
    }

    filtered = filtered.filter(event => {
      const category = (event?.dateCategory || 'future') as 'past' | 'today' | 'future';
      return this.dateCategoryFilters[category];
    });

    const activeStatuses = this.getSelectedStatuses();
    if (activeStatuses.size > 0 && activeStatuses.size < this.availableStatuses.length) {
      filtered = filtered.filter(event => {
        const statusKey = event?.statusKey || this.normalizeStatusKey(event?.status);
        return activeStatuses.has(statusKey);
      });
    } else if (activeStatuses.size === 0 && this.availableStatuses.length > 0) {
      filtered = [];
    }

    const query = this.searchQuery.trim().toLowerCase();
    if (query) {
      const terms = query.split(/\s+/).filter(Boolean);
      const weights = {
        title: 5,
        id: 4,
        requestor: 3,
        location: 2,
        description: 1
      };

      filtered = filtered
        .map((event, index) => {
          const title = String(event?.displayTitle || '').toLowerCase();
          const id = String(event?.event_id ?? '').toLowerCase();
          const requestor = String(event?.requestor || event?.originalRequestor || '').toLowerCase();
          const leader = String(event?.event_leader?.name || event?.event_leader?.email || event?.eventLeader || '').toLowerCase();
          const location = String(event?.primary_presenting_location || '').toLowerCase();
          const description = String(event?.event_description || '').toLowerCase();
          const requestorLeader = `${requestor} ${leader}`.trim();

          let score = 0;
          for (const term of terms) {
            let termScore = 0;
            if (title.includes(term)) termScore = Math.max(termScore, weights.title);
            if (id.includes(term)) termScore = Math.max(termScore, weights.id);
            if (requestorLeader.includes(term)) termScore = Math.max(termScore, weights.requestor);
            if (location.includes(term)) termScore = Math.max(termScore, weights.location);
            if (description.includes(term)) termScore = Math.max(termScore, weights.description);
            if (termScore === 0) {
              return null;
            }
            score += termScore;
          }

          return { event, score, index };
        })
        .filter((entry): entry is { event: any; score: number; index: number } => !!entry)
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return a.index - b.index;
        })
        .map(entry => entry.event);
    }

    this.currentEvents = filtered;
  }

  isDateFilterAllSelected(): boolean {
    return this.dateCategoryFilters.past && this.dateCategoryFilters.today && this.dateCategoryFilters.future;
  }

  shouldShowStatsRatio(): boolean {
    return this.currentEvents.length !== this.allEvents.length;
  }

  private getDateFilteredEvents(): any[] {
    return this.getStatusFilteredEvents().filter(event => {
      const category = (event?.dateCategory || 'future') as 'past' | 'today' | 'future';
      return this.dateCategoryFilters[category];
    });
  }

  private getStatusFilteredEvents(): any[] {
    const activeStatuses = this.getSelectedStatuses();
    if (this.availableStatuses.length === 0) {
      return [...this.allEvents];
    }

    if (activeStatuses.size === 0) {
      return [];
    }

    if (activeStatuses.size === this.availableStatuses.length) {
      return [...this.allEvents];
    }

    return this.allEvents.filter(event => {
      const statusKey = event?.statusKey || this.normalizeStatusKey(event?.status);
      return activeStatuses.has(statusKey);
    });
  }

  getTotalCount(): number {
    return this.getStatusFilteredEvents().length;
  }

  getDisplayedTotalCount(): number {
    return this.currentEvents.length;
  }

  getDisplayedPendingCount(): number {
    return this.currentEvents.filter(event =>
      event.status?.toLowerCase() === 'pending'
    ).length;
  }

  getDisplayedApprovedCount(): number {
    return this.currentEvents.filter(event =>
      event.status?.toLowerCase() === 'approved'
    ).length;
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      // Check if the date is valid
      if (isNaN(date.getTime())) return 'Invalid date';
      
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  }

  formatTime(startDate: string, endDate: string): string {
    if (!startDate) return '';
    
    try {
      const start = new Date(startDate);
      // Check if the start date is valid
      if (isNaN(start.getTime())) return 'Invalid time';
      
      const startTime = start.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      
      if (endDate) {
        const end = new Date(endDate);
        // Check if the end date is valid
        if (!isNaN(end.getTime())) {
          const endTime = end.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
          return `${startTime} - ${endTime}`;
        }
      }
      
      return startTime;
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'Invalid time';
    }
  }

  getStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'unsubmitted':
        return 'status-unsubmitted';
      case 'approved':
      case 'completed':
        return 'status-approved';
      case 'pending':
        return 'status-pending';
      case 'rejected':
      case 'cancelled':
      case 'canceled':
        return 'status-canceled';
      case 'in progress':
      case 'processing':
        return 'status-in-progress';
      default:
        return 'status-default';
    }
  }

  editEvent(event: any): void {
    console.log('Editing event:', event, " with ID:", event.event_id);
    // Routes to #/request?id=event_id
    const tree = this.router.createUrlTree(['/request'], { queryParams: { id: event.event_id } });
    const url = this.location.prepareExternalUrl(this.router.serializeUrl(tree));
    window.open(url, '_blank');
  }

  async deleteEvent(event: any, clickEvent?: Event) {
    // Prevent event bubbling to avoid opening the request
    if (clickEvent) {
      clickEvent.stopPropagation();
      clickEvent.preventDefault();
    }

    if (!this.user?.isAdmin) {
      await this._alert.error('You do not have permission to delete events.');
      return;
    }

    if (!event.event_id) {
      await this._alert.error('Cannot delete event: Event ID not found.');
      return;
    }

    const confirmed = await this._alert.confirm(
      `Are you sure you want to delete this event?\n\nTitle: ${event.request_title || event.displayTitle || 'Untitled'}\nID: #${event.event_id}\nRequestor: ${event.requestor?.name || event.requestor?.email || event.requestor || 'Unknown'}\n\nThis action cannot be undone.`,
      'Delete Event'
    );

    if (confirmed) {
      this._db.deleteEventById(event.event_id).subscribe({
        next: async (response) => {
          await this._alert.success('Event deleted successfully.');
          this.loadCurrentEvents(); // Reload the list
        },
        error: async (error) => {
          console.error('Error deleting event:', error);
          await this._alert.error('Failed to delete event. Please try again.', this._alert.getErrorDetails(error));
        }
      });
    }
  }

  // Quick approval/rejection methods
  async approveEvent(event: any) {
    console.log('Approving event - Full event object:', event);
    
    // Validate event ID first
    if (!event.event_id) {
      console.error('No event_id found in event object');
      this.showErrorMessage('Error: Event ID is missing. Cannot approve event.');
      return;
    }

    const confirmed = await this._alert.confirm(`Are you sure you want to approve the event "${event.displayTitle}"?`);
    
    if (confirmed) {
      console.log('Event ID confirmed:', event.event_id);
      
      const newWorkflow = event.workflow ? event.workflow + ' -> Approved' : 'Submitted -> Approved';
      console.log('New workflow:', newWorkflow);

      // Convert event object to form format expected by generateEventPayload
      const formData = this.convertEventToFormData(event);
      console.log('Converted form data:', formData);

      // Use the database service's generateEventPayload method to create proper payload
      const payload = this._db.generateEventPayload(formData, newWorkflow);
      
      // Override the request_status to ensure it's set to Approved
      payload.request_status = 'Approved';
      payload.workflow = newWorkflow;

      console.log('Generated payload for approval:', payload);
      console.log('API URL will be:', `https://bsa-api.musea2.azure.ext.gm.com/api/request/update/${event.event_id}`);

      this._db.updateEventById(event.event_id, payload).subscribe({
        next: async (response) => {
          console.log('Event approved - Raw response:', response);
          
          // Update the local event status regardless of response format
          const eventIndex = this.currentEvents.findIndex(e => e.event_id === event.event_id);
          if (eventIndex !== -1) {
            this.currentEvents[eventIndex].status = 'Approved';
            this.currentEvents[eventIndex].request_status = 'Approved';
            this.currentEvents[eventIndex].workflow = newWorkflow;
          }
          
          // Show success message
          console.log('Success: Event approved successfully');
          await this._alert.success(`Event "${event.displayTitle}" has been approved successfully!`);
        },
        error: async (error) => {
          console.error('Error approving event:', error);
          console.error('Error details:', {
            status: error.status,
            statusText: error.statusText,
            message: error.message,
            error: error.error
          });
          
          // Check if it's actually a success disguised as an error
          if (error.status === 200 || error.status === 201 || error.status === 204) {
            console.log('API returned success status despite error handler being called');
            // Update the local event status
            const eventIndex = this.currentEvents.findIndex(e => e.event_id === event.event_id);
            if (eventIndex !== -1) {
              this.currentEvents[eventIndex].status = 'Approved';
              this.currentEvents[eventIndex].request_status = 'Approved';
              this.currentEvents[eventIndex].workflow = newWorkflow;
            }
            await this._alert.success(`Event "${event.displayTitle}" has been approved successfully!`);
          } else {
            await this._alert.success('Failed to approve event. Please try again.');
          }
        }
      });
    }
  }

  async rejectEvent(event: any) {
    console.log('Rejecting event - Full event object:', event);
    
    // Validate event ID first
    if (!event.event_id) {
      console.error('No event_id found in event object');
      this.showErrorMessage('Error: Event ID is missing. Cannot reject event.');
      return;
    }

    const confirmed = await this._alert.confirm(`Are you sure you want to reject the event "${event.displayTitle}"?`);
    
    if (confirmed) {
      console.log('Event ID confirmed:', event.event_id);
      
      const newWorkflow = event.workflow ? event.workflow + ' -> Canceled' : 'Submitted -> Canceled';
      console.log('New workflow:', newWorkflow);

      // Convert event object to form format expected by generateEventPayload
      const formData = this.convertEventToFormData(event);
      console.log('Converted form data:', formData);

      // Use the database service's generateEventPayload method to create proper payload
      const payload = this._db.generateEventPayload(formData, newWorkflow);
      
      // Override the request_status to ensure it's set to Canceled
      payload.request_status = 'Canceled';
      payload.workflow = newWorkflow;

      console.log('Generated payload for rejection:', payload);

      this._db.updateEventById(event.event_id, payload).subscribe({
        next: async (response) => {
          console.log('Event canceled - Raw response:', response);
          
          // Update the local event status regardless of response format
          const eventIndex = this.currentEvents.findIndex(e => e.event_id === event.event_id);
          if (eventIndex !== -1) {
            this.currentEvents[eventIndex].status = 'Canceled';
            this.currentEvents[eventIndex].request_status = 'Canceled';
            this.currentEvents[eventIndex].workflow = newWorkflow;
          }
          
          // Show success message
          console.log('Success: Event canceled successfully');
          await this._alert.success(`Event "${event.displayTitle}" has been canceled successfully!`);
        },
        error: async (error) => {
          console.error('Error canceling event:', error);
          console.error('Error details:', {
            status: error.status,
            statusText: error.statusText,
            message: error.message,
            error: error.error
          });
          
          // Check if it's actually a success disguised as an error
          if (error.status === 200 || error.status === 201 || error.status === 204) {
            console.log('API returned success status despite error handler being called');
            // Update the local event status
            const eventIndex = this.currentEvents.findIndex(e => e.event_id === event.event_id);
            if (eventIndex !== -1) {
              this.currentEvents[eventIndex].status = 'Canceled';
              this.currentEvents[eventIndex].request_status = 'Canceled';
              this.currentEvents[eventIndex].workflow = newWorkflow;
            }
            await this._alert.success(`Event "${event.displayTitle}" has been canceled successfully!`);
          } else {
            await this._alert.error('Failed to cancel event. Please try again.', this._alert.getErrorDetails(error));
          }
        }
      });
    }
  }
  downloadCSV() {
    const rows = this.currentEvents.map(event => {
      const requestorInfo = this.parsePersonValue(event.requestor);
      const eventLeaderInfo = this.parsePersonValue(event.event_leader || event.eventLeader);

      return {
        ...event,
        start_date: this.formatDateForExcel(event.start_date),
        end_date: this.formatDateForExcel(event.end_date),
        vod_expiration_date: this.formatDateForExcel(event.vod_expiration_date),
        formattedStartDate: this.formatDateForExcel(event.formattedStartDate),
        requestor: requestorInfo.name || event.requestor || '',
        requestor_email: requestorInfo.email || '',
        event_leader: eventLeaderInfo.name || event.event_leader || event.eventLeader || '',
        event_leader_email: eventLeaderInfo.email || ''
      };
    });
    this._csv.downloadJsonAsCsv(rows, { filename: 'eventsExport.csv' });
  }

  private parsePersonValue(value: any): { name: string; email: string; gmid: string } {
    if (!value) return { name: '', email: '', gmid: '' };

    if (typeof value === 'object') {
      return {
        name: String(value.name || '').trim(),
        email: String(value.email || '').trim(),
        gmid: String(value.gmid || '').trim()
      };
    }

    const raw = String(value).trim();
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return {
          name: String(parsed.name || '').trim(),
          email: String(parsed.email || '').trim(),
          gmid: String(parsed.gmid || '').trim()
        };
      }
    } catch {
      // Fall through to plain string handling.
    }

    if (raw.includes('@')) {
      return { name: '', email: raw, gmid: '' };
    }

    return { name: raw, email: '', gmid: '' };
  }

  // Format date for Excel compatibility
  formatDateForExcel(dateString: string | null | undefined): string {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      // Format as m/d/yyyy h:mm:ss which Excel recognizes as a date with time
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const year = date.getFullYear();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const seconds = date.getSeconds();
      
      return `${month}/${day}/${year} ${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } catch (error) {
      return '';
    }
  }

  // Helper methods for user feedback
  async showSuccessMessage(message: string) {
    // You can implement toast notifications here
    console.log('Success:', message);
    await this._alert.success(message); // Temporary - replace with proper toast
  }

  async showErrorMessage(message: string){
    // You can implement toast notifications here
    console.error('Error:', message);
    await this._alert.error(message); // Temporary - replace with proper toast
  }

  // Check if event can be approved/rejected
  canApprove(event: any): boolean {
    return event.status?.toLowerCase() === 'pending';
  }

  canReject(event: any): boolean {
    const status = event.status?.toLowerCase();
    return status === 'pending' || status === 'submitted';
  }

  // Count methods for stats
  getPendingCount(): number {
    return this.allEvents.filter(event => 
      event.status?.toLowerCase() === 'pending'
    ).length;
  }

  getApprovedCount(): number {
    return this.allEvents.filter(event => 
      event.status?.toLowerCase() === 'approved'
    ).length;
  }

  trackByEventId(index: number, event: any): any {
    return event.id || index;
  }

  // Get relative time description (e.g., "Today", "Tomorrow", "In 3 days")
  getRelativeTimeDescription(event: any): string {
    if (!event.formattedStartDate) return '';
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const eventDate = new Date(event.formattedStartDate);
    eventDate.setHours(0, 0, 0, 0);
    
    const diffTime = eventDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays > 1) return `In ${diffDays} days`;
    if (diffDays < -1) return `${Math.abs(diffDays)} days ago`;
    
    return '';
  }

  // Check if event is happening today
  isEventToday(event: any): boolean {
    if (!event.formattedStartDate) return false;
    
    const today = new Date();
    const eventDate = new Date(event.formattedStartDate);
    
    return today.toDateString() === eventDate.toDateString();
  }

  // Check if event is happening this week
  isEventInFuture(event: any): boolean {
    if (!event.formattedStartDate) return false;
    if (this.isEventToday(event)) return false;
    
    const today = new Date();
    const eventDate = new Date(event.formattedStartDate);
    
    // Get start of this week (Monday)
    // const startOfWeek = new Date(today);
    // const day = startOfWeek.getDay();
    // const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    // startOfWeek.setDate(diff);
    // startOfWeek.setHours(0, 0, 0, 0);
    
    // // Get end of this week (Sunday)
    // const endOfWeek = new Date(startOfWeek);
    // endOfWeek.setDate(startOfWeek.getDate() + 6);
    // endOfWeek.setHours(23, 59, 59, 999);
    
    return eventDate > today;
  }

  // Convert database event object to form data format expected by generateEventPayload
  private convertEventToFormData(event: any): any {
    // Extract date and time from start_date and end_date
    let eventStartDate = '';
    let eventStartTime = '';
    let eventEndTime = '';
    
    if (event.start_date) {
      const startDate = new Date(event.start_date);
      if (!isNaN(startDate.getTime())) {
        eventStartDate = startDate.toISOString().split('T')[0]; // YYYY-MM-DD
        // Format time as HH:MM for 24-hour format
        const hours = startDate.getHours().toString().padStart(2, '0');
        const minutes = startDate.getMinutes().toString().padStart(2, '0');
        eventStartTime = `${hours}:${minutes}`;
      }
    }
    
    if (event.end_date) {
      const endDate = new Date(event.end_date);
      if (!isNaN(endDate.getTime())) {
        // Format time as HH:MM for 24-hour format
        const hours = endDate.getHours().toString().padStart(2, '0');
        const minutes = endDate.getMinutes().toString().padStart(2, '0');
        eventEndTime = `${hours}:${minutes}`;
      }
    }

    console.log('Date/Time conversion:', {
      originalStart: event.start_date,
      originalEnd: event.end_date,
      convertedDate: eventStartDate,
      convertedStartTime: eventStartTime,
      convertedEndTime: eventEndTime
    });

    return {
      event_id: event.event_id,
      requestType: event.request_type || '',
      expectedNumberOfParticipants: event.expected_number_of_participants || '',
      requestTitle: event.request_title || '',
      eventStartDate: eventStartDate,
      eventStartTime: eventStartTime,
      eventEndTime: eventEndTime,
      primaryPresentingLocation: event.primary_presenting_location || '',
      otherLocation: event.other_location || '',
      recordingLocation: event.recording_location || '',
      otherRecordingLocation: event.other_recording_location || '',
      additionalDetailForLocation: event.additional_detail_for_location || '',
      remotePresentingLocation: event.remote_presenting_location || '',
      requestor: event.requestor?.email || event.requestor?.name || event.requestor || '',
      eventLeader: event.event_leader?.email || event.event_leader?.name || event.event_leader || '',
      responsibleForEditing: event.responsible_for_editing || '',
      eventRequirements: event.event_requirements || '',
      setItems: event.set_items || '',
      eventDescription: event.event_description || '',
      presenters: event.presenters || '',
      audience: event.audience || '',
      audienceOther: event.audience_other || '',
      videoSharingPlatforms: event.video_sharing_platforms || '',
      videoFileRecipient: event.video_file_recipient || '',
      region: event.region || '',
      takeQuestions: event.how_will_you_take_questions || '',
      questionsOther: event.how_will_you_take_questions_other || '',
      isVideoOnDemandNeeded: event.is_video_on_demand_needed || 'No',
      vodExpirationDate: event.vod_expiration_date || '',
      dataClassification: event.data_classification || '',
      retentionTime: event.retention_time || '',
      assignedTo: event.assigned_to || '',
      tech: event.tech || '',
      highProfileEvent: event.high_profile_event || 'No',
      captioning: event.captioning || 'No',
      workRelatedEvent: event.work_related_event || 'Yes',
      testing: event.testing || 'Yes',
      toolsToBeUsed: event.tools_to_be_used || '',
      programCode: event.program_code || '',
      custNumber: event.cust_number || '',
      studioNeeds: event.studio_needs || '',
      broadcastComments: event.broadcast_comments || '',
      gmVideoUrl: event.gm_video_url || '',
      virtualEventsUrl: event.virtual_events_url || '',
      brightcoveUrl: event.brightcove_url || '',
      teamsUrl: event.teams_url || '',
      sendToArchive: event.send_to_archive || 'No'
    };
  }

  // Get display-friendly status text
  getDisplayStatus(status: string): string {
    if (!status) return 'Unknown';
    
    const statusLower = status.toLowerCase();
    if (statusLower === 'unsubmitted') {
      return 'Unsubmitted';
    }
    if (statusLower === 'rejected' || statusLower === 'cancelled' || statusLower === 'canceled') {
      return 'Canceled';
    }
    
    // Capitalize first letter of other statuses
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  }

  toggleStatusDropdown(): void {
    this.statusDropdownOpen = !this.statusDropdownOpen;
  }

  toggleStatusFilter(statusKey: string): void {
    this.statusFilters[statusKey] = !this.statusFilters[statusKey];
    this.syncActiveFilterFromStatusFilters();
    this.applyFilter();
  }

  selectAllStatuses(): void {
    this.availableStatuses.forEach(status => {
      this.statusFilters[status] = true;
    });
    this.syncActiveFilterFromStatusFilters();
    this.applyFilter();
  }

  clearAllStatuses(): void {
    this.availableStatuses.forEach(status => {
      this.statusFilters[status] = false;
    });
    this.syncActiveFilterFromStatusFilters();
    this.applyFilter();
  }

  getStatusSummary(): string {
    if (this.availableStatuses.length === 0) return 'All';
    const selected = this.getSelectedStatuses();
    if (selected.size === 0) return 'None';
    if (selected.size === this.availableStatuses.length) return 'All';
    if (selected.size === 1) return this.getDisplayStatus([...selected][0]);
    return `${selected.size} selected`;
  }

  private syncStatusFiltersFromActiveFilter(): void {
    if (this.availableStatuses.length === 0) return;

    if (this.activeFilter === 'all') {
      this.availableStatuses.forEach(status => {
        this.statusFilters[status] = true;
      });
      return;
    }

    const target = this.activeFilter;
    this.availableStatuses.forEach(status => {
      this.statusFilters[status] = status === target;
    });
  }

  private syncActiveFilterFromStatusFilters(): void {
    if (this.availableStatuses.length === 0) {
      this.activeFilter = 'all';
      return;
    }

    const selected = this.getSelectedStatuses();
    if (selected.size === this.availableStatuses.length) {
      this.activeFilter = 'all';
      return;
    }

    if (selected.size === 1) {
      const only = [...selected][0];
      if (only === 'pending' || only === 'approved') {
        this.activeFilter = only;
        return;
      }
    }

    this.activeFilter = 'all';
  }

  trackByStatus = (_: number, status: string) => status;

  private updateStatusFilters(events: any[]): void {
    const statusSet = new Set<string>();
    for (const event of events) {
      const statusKey = event?.statusKey || this.normalizeStatusKey(event?.status);
      if (statusKey) statusSet.add(statusKey);
    }

    const mergedStatuses = Array.from(statusSet);
    const ordered = this.sortStatuses(mergedStatuses);

    const updated: Record<string, boolean> = {};
    ordered.forEach(status => {
      if (Object.prototype.hasOwnProperty.call(this.statusFilters, status)) {
        updated[status] = this.statusFilters[status];
      } else {
        updated[status] = true;
      }
    });

    this.availableStatuses = ordered;
    this.statusFilters = updated;
  }

  private getSelectedStatuses(): Set<string> {
    const selected = new Set<string>();
    for (const status of this.availableStatuses) {
      if (this.statusFilters[status]) {
        selected.add(status);
      }
    }
    return selected;
  }

  private normalizeStatusKey(status: any): string {
    const raw = String(status || '').trim().toLowerCase();
    if (!raw) return 'unknown';
    if (raw === 'cancelled' || raw === 'rejected') return 'canceled';
    return raw;
  }

  private sortStatuses(statuses: string[]): string[] {
    const priority = ['unsubmitted', 'pending', 'submitted', 'approved', 'completed', 'in progress', 'processing', 'rejected', 'canceled', 'unknown'];
    const order = new Map(priority.map((value, index) => [value, index]));
    return [...statuses].sort((a, b) => {
      const aOrder = order.has(a) ? order.get(a)! : priority.length;
      const bOrder = order.has(b) ? order.get(b)! : priority.length;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.localeCompare(b);
    });
  }

  displayValue(value: any): string {
    if (value === 0) return '0';
    if (value === false) return 'false';
    if (value === null || value === undefined) return 'BLANK';
    const text = String(value).trim();
    return text ? text : 'BLANK';
  }

  isBlankValue(value: any): boolean {
    if (value === 0 || value === false) return false;
    if (value === null || value === undefined) return true;
    return String(value).trim() === '';
  }

  getParticipantsDisplay(value: any): string {
    if (this.isBlankValue(value)) return 'BLANK';
    return `${value} participants`;
  }

  // Open CSV upload dialog
  openCsvUploadDialog(): void {
    const dialogRef = this.dialog.open(CsvUploadDialogComponent, {
      width: '95vw',
      maxWidth: '95vw',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Refresh events after successful upload
        this.loadCurrentEvents();
      }
    });
  }

  hasMetrics(event: any): boolean {
    return (
      Number(event?.gm_video_attendees) > 0 ||
      Number(event?.teams_attendees) > 0 ||
      Number(event?.virtual_events_attendees) > 0 ||
      Number(event?.venue_attendees) > 0
    );
  }

  openMetricsDialog(event: any, clickEvent: Event): void {
    clickEvent.stopPropagation();
    clickEvent.preventDefault();
    this.dialog.open(EventMetricsDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      data: {
        eventTitle: event.displayTitle || event.request_title || 'Untitled Event',
        eventId: event.event_id,
        gm_video_attendees: event.gm_video_attendees || 0,
        teams_attendees: event.teams_attendees || 0,
        virtual_events_attendees: event.virtual_events_attendees || 0,
        venue_attendees: event.venue_attendees || 0,
      }
    });
  }
}
